import posthog, { type JsonType } from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import React, { useEffect, useMemo, useRef } from 'react';

import { PlatformTypesLookup } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { bootstrappedFlags } from '@eluve/posthog';
import { useUserIdFromSession } from '@eluve/session-helpers';

import {
  AnalyticsInstance,
  CoreAnalyticsProvider,
} from './CoreAnalyticsProvider';
import { POSTHOG_ANONYMOUS_ID_KEY } from './constants';
import { useFlagRetrievalMetrics } from './flagRetrievalMetrics';

export interface AnalyticsProviderProps {
  children: React.ReactNode;
  apiKey: string;
  apiHost: string;
  disableExternalDependencies?: boolean;
  bootstrapFlags?: Record<string, string | boolean>;
  bootstrapFlagPayloads?: Record<string, JsonType>;
  /**
   * Identity (`userId:tenantId`) that `bootstrapFlags` belongs to. Echoed back
   * through `onOverrideApplied` once the override has actually been pushed
   * into PostHog, so a caller can hold its subtree until then. [ENG-5545]
   */
  overrideKey?: string;
  /**
   * Fired after `overrideFeatureFlags` has run for `overrideKey`. Applying the
   * override is an effect, so without this a consumer of the flags can render
   * and commit once against the PREVIOUS identity's overrides — on a tenant
   * A→B switch, B's children would evaluate under A's gates.
   */
  onOverrideApplied?: (overrideKey: string) => void;
}

/**
 * Inner component that runs inside PostHogProvider to access the PostHog instance
 * and provide it to CoreAnalyticsProvider
 */
const CoreAnalyticsProviderBridge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const postHogInstance = usePostHog();

  const analyticsInstance = useMemo<AnalyticsInstance>(() => {
    return {
      capture: postHogInstance.capture.bind(postHogInstance),
      isFeatureEnabled: (flagKey) => postHogInstance.isFeatureEnabled(flagKey),
      getFeatureFlagPayload: (flagKey) =>
        postHogInstance.getFeatureFlagPayload(flagKey),
    };
  }, [postHogInstance]);

  return (
    <CoreAnalyticsProvider analyticsInstance={analyticsInstance}>
      {children}
    </CoreAnalyticsProvider>
  );
};

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  apiKey,
  apiHost,
  disableExternalDependencies = false,
  bootstrapFlags,
  bootstrapFlagPayloads,
  overrideKey,
  onOverrideApplied,
}) => {
  const logger = useNamedLogger('AnalyticsProvider');
  const userId = useUserIdFromSession();
  const [posted, setPosted] = React.useState(false);
  const [loaded, setLoaded] = React.useState(posthog.__loaded);
  const bootstrapFlagsRef = useRef(bootstrapFlags);
  bootstrapFlagsRef.current = bootstrapFlags;
  const bootstrapFlagPayloadsRef = useRef(bootstrapFlagPayloads);
  bootstrapFlagPayloadsRef.current = bootstrapFlagPayloads;
  // Held in a ref so an inline callback from the caller can't re-run the
  // override effect on every render.
  const onOverrideAppliedRef = useRef(onOverrideApplied);
  onOverrideAppliedRef.current = onOverrideApplied;

  useFlagRetrievalMetrics(apiHost, logger);

  const postHogConfig = useMemo(
    () => ({
      bootstrap: {
        distinctID: userId,
        isIdentifiedID: true,
        featureFlags:
          bootstrapFlagsRef.current ?? bootstrappedFlags.featureFlags,
        featureFlagPayloads: bootstrapFlagsRef.current
          ? (bootstrapFlagPayloadsRef.current ?? {})
          : bootstrappedFlags.featureFlagPayloads,
      },
      api_host: apiHost,
      autocapture: false,
      person_profiles: 'identified_only' as const,
      opt_in_site_apps: true,
      ...(disableExternalDependencies && {
        disable_external_dependency_loading: true,
      }),
      session_recording: {
        maskTextSelector: '.privacy-text, .tiptap *, .posthog-privacy-text',
      },
    }),
    [userId, apiHost, disableExternalDependencies],
  );

  useEffect(() => {
    if (!userId || posted) return;

    const storedPostHogId = localStorage.getItem(POSTHOG_ANONYMOUS_ID_KEY);
    const currentPostHogId = posthog.get_distinct_id();

    if (!storedPostHogId) return;

    if (storedPostHogId !== currentPostHogId) {
      posthog.alias(storedPostHogId, userId);
    }

    setPosted(true);
    localStorage.removeItem(POSTHOG_ANONYMOUS_ID_KEY);
  }, [logger, posted, userId]);

  useEffect(() => {
    if (!loaded) return;
    posthog.set_config(postHogConfig);
  }, [loaded, postHogConfig]);

  useEffect(() => {
    if (!loaded) return;
    // Bootstrap config is consumed only on initial PostHog init; once loaded,
    // refreshes from the SWR cache layer must be pushed explicitly.
    // overrideFeatureFlags persists to PostHog's own localStorage, so when we
    // have no flags (e.g. cold-load fetch failure with a leftover override
    // from a prior session) we clear it to keep the two stores in sync.
    if (bootstrapFlags) {
      posthog.featureFlags.overrideFeatureFlags({
        flags: bootstrapFlags,
        payloads: bootstrapFlagPayloads ?? {},
        suppressWarning: true,
      });
    } else {
      posthog.featureFlags.overrideFeatureFlags(false);
    }
    if (overrideKey !== undefined) {
      onOverrideAppliedRef.current?.(overrideKey);
    }
  }, [loaded, bootstrapFlags, bootstrapFlagPayloads, overrideKey]);

  useEffect(
    // The override lives on the global PostHog instance and persists to its
    // localStorage, so unmounting this provider (logout, teardown) would
    // otherwise leave the previous identity's flags in place for whatever
    // mounts next.
    () => () => {
      posthog.featureFlags.overrideFeatureFlags(false);
    },
    [],
  );

  useEffect(() => {
    if (!loaded) return;
    // userId in deps so logout's posthog.reset() doesn't leave us untagged.
    posthog.register({ platform: PlatformTypesLookup.WEB });
  }, [loaded, userId]);

  if (loaded) {
    return (
      <PostHogProvider client={posthog}>
        <CoreAnalyticsProviderBridge>{children}</CoreAnalyticsProviderBridge>
      </PostHogProvider>
    );
  }

  return (
    <PostHogProvider
      apiKey={apiKey}
      options={{
        ...postHogConfig,
        loaded: () => {
          logger.info('Posthog loaded');
          setLoaded(true);
        },
      }}
    >
      <CoreAnalyticsProviderBridge>{children}</CoreAnalyticsProviderBridge>
    </PostHogProvider>
  );
};
